import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Skull, Loader2 } from "lucide-react";

export const AuthDialog = ({ open, onOpenChange }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ username: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back to the fog.");
      } else {
        await register(form.username, form.email, form.password);
        toast.success("Account created. The trial begins.");
      }
      onOpenChange(false);
      setForm({ username: "", email: "", password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitLabel = mode === "login" ? "Sign In" : "Create Account";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm border-white/10 bg-card" data-testid="auth-dialog">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2">
            <Skull className="h-6 w-6 text-primary" />
            <DialogTitle className="font-display text-3xl tracking-wider">
              {mode === "login" ? "Enter the Fog" : "Join the Hunt"}
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-foreground/60">
            {mode === "login" ? "Sign in to track challenges, save ideas and climb the leaderboard." : "Create a free account to save your work and join the community."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-foreground/70">Username</Label>
              <Input data-testid="auth-username" required value={form.username} onChange={set("username")} placeholder="FogWalker" className="rounded-sm border-white/10 bg-background" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-foreground/70">Email</Label>
            <Input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@dbd.com" className="rounded-sm border-white/10 bg-background" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-foreground/70">Password</Label>
            <Input data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} placeholder="••••••••" className="rounded-sm border-white/10 bg-background" />
          </div>

          <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full rounded-sm bg-primary font-bold uppercase tracking-widest hover:bg-primary/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
          </Button>
        </form>

        <div className="flex items-center gap-3 py-1">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] uppercase tracking-widest text-foreground/40">or</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <Button data-testid="auth-google" variant="outline" onClick={googleLogin} className="w-full rounded-sm border-white/10 bg-transparent font-bold uppercase tracking-widest hover:border-primary/50 hover:bg-primary/10">
          Continue with Google
        </Button>

        <p className="text-center text-sm text-foreground/60">
          {mode === "login" ? "No account yet?" : "Already a creator?"}{" "}
          <button data-testid="auth-toggle" type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-bold text-primary hover:underline">
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
